import { first } from "lodash/fp";

import { Menu } from "@mui/material";
import styled from "@mui/material/styles/styled";

import VideoIconButton from "@app/analytics/components/VideoIconButton";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

const StyledMenu = styled(Menu)(({ theme }) => ({
  borderRadius: "8px",
  "& .MuiList-root": {
    backgroundColor: "#000000CC",
    color: "#FFF",
    padding: theme.spacing(2),
  },
}));

export interface PlayerControlsSettingsProps {
  renderSettings?: () => React.ReactElement;
  targetClassName?: string;
}

const PlayerControlsSettings = ({
  renderSettings,
  targetClassName,
}: PlayerControlsSettingsProps) => {
  const { onClose, onClick, open, anchorElement } = useAnchorElement();

  if (!renderSettings) return null;

  const targetElement = targetClassName
    ? first(Array.from(document.getElementsByClassName(targetClassName)))
    : undefined;
  const focusedClassName = `${targetClassName}--focused`;

  const handleClick: typeof onClick = (e) => {
    onClick(e);

    // add focused classname to target element (if classname provided/element exists)
    // so any focus/hover CSS styles can be applied to that element
    if (targetElement) {
      targetElement.className = `${targetElement.className} ${focusedClassName}`;
    }
  };

  const handleClose = () => {
    onClose();

    if (targetElement) {
      // short timeout otherwise there might be a flicker between clicking off the settings
      // menu and into the video/activating the video's hover state.
      setTimeout(() => {
        targetElement.className = targetElement.className
          .replace(focusedClassName, "")
          .trim();
      }, 300);
    }
  };

  return (
    <>
      <VideoIconButton
        icon="settings"
        tooltip="Settings"
        onClick={handleClick}
      />
      <StyledMenu
        open={open}
        onClose={handleClose}
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {renderSettings()}
      </StyledMenu>
    </>
  );
};

export default PlayerControlsSettings;
